import dynamic from 'next/dynamic'
import React, { useEffect, useState } from 'react'
import { Form } from 'react-final-form'
import { useToasts } from 'react-toast-notifications'

import escape from 'lodash-es/escape'
import remove from 'lodash-es/remove'
import shuffle from 'lodash-es/shuffle'
import { object } from 'yup'

import { Button } from '@infura/packages.ui/Button'
import { Modal } from '@infura/packages.ui/Modal'
import { Text } from '@infura/packages.ui/Text'

import {
  ISurveyOption,
  ISurveyQuestion,
  SurveyProgress,
  onboardingSurveyModalQuestions,
  useSurvey
} from 'components/onboardingSurvey'

import { api } from 'lib/api/api'
import { onboardingCategoryValidator, onboardingRoleValidator, onboardingUsageValidator } from 'lib/forms/validators'
import handleSubmissionError from 'lib/handleSubmissionError'

const validationSchema = [
  object().shape({
    ...onboardingRoleValidator,
    ...onboardingUsageValidator
  }),
  object().shape({
    ...onboardingCategoryValidator
  })
]

const DEFAULT_COMPONENT = 'Question'

const NewSurveyModal = ({ hideOnboardingNudge, isSurveyVisible, toggleSurveyVisible, userId }) => {
  const surveyState = useSurvey({
    questions: onboardingSurveyModalQuestions
  })
  const { currentQuestion, currentStep, handleNext, handlePrevious, isLastPage, steps, validate } = surveyState
  const [stateCurrentQuestion, setStateCurrentQuestion] = useState<Array<ISurveyQuestion>>([])
  const [loading, setLoading] = useState(false)
  const { addToast } = useToasts()
  const SurveyComponent = dynamic(() => import(`./${currentQuestion.component ?? DEFAULT_COMPONENT}`))

  useEffect(() => {
    if (!stateCurrentQuestion[currentStep]) {
      const shuffledQuestions =
        currentQuestion.component === DEFAULT_COMPONENT
          ? {
              ...currentQuestion,
              datas: currentQuestion.datas.map(data => {
                // Let option "other" at the end of questions and shuffle others
                const latestOption = remove(data.options as Array<ISurveyOption>, el => el.id === 'other')
                return {
                  ...data,
                  options: [...shuffle(data.options), ...latestOption]
                }
              })
            }
          : currentQuestion
      const tmpState = stateCurrentQuestion
      tmpState[currentStep] = shuffledQuestions
      setStateCurrentQuestion(tmpState)
    }
  }, [currentStep, currentQuestion])

  const handleSubmit = async values => {
    try {
      if (isLastPage) {
        setLoading(true)
        const response = Object.keys(values).find(key => key.includes('category_other'))

        const sanitizeValues = {
          ...values,
          ...(response && { category_other: escape(values[response]) })
        }

        await api
          .updateCustomerOnboarding(userId, {
            ...sanitizeValues
          })
          .then(() => {
            toggleSurveyVisible()
            hideOnboardingNudge()
            addToast('Thank you! Survey submitted.', {
              appearance: 'success',
              autoDismiss: true
            })
          })
          .finally(() => {
            setLoading(false)
          })
      } else {
        handleNext()
      }
    } catch (e: any) {
      return handleSubmissionError(e)
    }
  }

  return (
    <Modal
      heading='Unlock a more personalized Infura experience'
      open={isSurveyVisible}
      setIsOpen={toggleSurveyVisible}
      width='xl'
    >
      <Text className='mb-0'>
        Quickly tell us your role and how you use our product. This will help us provide you the best experience
        possible.
      </Text>
      <Form
        initialValues={{ category: 'other' }}
        mutators={{
          setTextToEmpty: (args, state, utils) => {
            utils.changeValue(state, args[0], () => '')
          }
        }}
        onSubmit={handleSubmit}
        render={({ handleSubmit, submitting }) => (
          <form className='w-full' onSubmit={handleSubmit}>
            <div className='pb-5'>
              <SurveyProgress
                currentStep={currentStep}
                data={currentQuestion['sideBarData']}
                steps={steps}
                stepsLabelHidden={true}
              />
            </div>
            {stateCurrentQuestion[currentStep]?.datas.map(data => (
              <div className='mb-2' key={data.id}>
                <SurveyComponent {...data} />
              </div>
            ))}
            <div className=''>
              {currentStep > 0 ? (
                <Button className='mr-4' onClick={handlePrevious} theme='secondary' type='button'>
                  Previous
                </Button>
              ) : null}
              <Button data-testid='survey-page-next' disabled={submitting} loading={loading} type='submit'>
                {currentQuestion['nextCTA']}
              </Button>
            </div>
          </form>
        )}
        // validate all step fields with Yup validation schema
        validate={values => validate(currentStep, values, validationSchema)}
      />
    </Modal>
  )
}

export default NewSurveyModal
